import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  double,
  decimal,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";

/**
 * Batch 1.5 — Payroll (reskin-docs/06). Money/economics = Admin/HR only (reskin-docs/07 §6).
 * Ported by reconciling each table's create + every up() ALTER across the 41 Payroll module
 * migrations (NOT just create blocks): company_id added after id (cascade) by 2022_09_02 to
 * payroll_settings/salary_groups/salary_components/salary_slips/salary_group_components/
 * employee_payroll_cycles/salary_payment_methods; salary_components +weekly/biweekly/semimonthly;
 * pay_codes +8 rate cols; payroll_settings.purchase_code added-then-DROPPED (not final);
 * salary_slips.currency_id retained (FK re-fixed 2023_01_01); employee_monthly_salaries +company_id
 * (2024_02_16) +annual/basic/fixed/effective cols. Note: legacy stores money strings on several
 * cols (basic_salary/net_salary/amount = varchar '0') — preserved verbatim.
 * Deferred cross-batch FKs (Finance 1.7): salary_slips.currency_id/expense_id, payroll_settings.currency_id.
 */

export const salaryGroups = mysqlTable("salary_groups", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  groupName: varchar("group_name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const salaryComponents = mysqlTable("salary_components", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  componentName: varchar("component_name", { length: 255 }).notNull(),
  componentType: mysqlEnum("component_type", ["earning", "deduction"]).notNull(),
  componentValue: varchar("component_value", { length: 255 }).notNull(),
  valueType: mysqlEnum("value_type", ["fixed", "percent"]).notNull(),
  weeklyValue: double("weekly_value").default(0).notNull(),
  biweeklyValue: double("biweekly_value").default(0).notNull(),
  semimonthlyValue: double("semimonthly_value").default(0).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** salary_tds — NO company_id (legacy). */
export const salaryTds = mysqlTable("salary_tds", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  salaryFrom: double("salary_from", { precision: 16, scale: 2 }).notNull(),
  salaryTo: double("salary_to", { precision: 16, scale: 2 }).notNull(),
  salaryPercent: double("salary_percent", { precision: 5, scale: 2 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const salaryGroupComponents = mysqlTable("salary_group_components", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  salaryGroupId: bigint("salary_group_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => salaryGroups.id, { onDelete: "cascade" }),
  salaryComponentId: bigint("salary_component_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => salaryComponents.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** employee_salary_groups — NO company_id (legacy). */
export const employeeSalaryGroups = mysqlTable("employee_salary_groups", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  salaryGroupId: bigint("salary_group_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => salaryGroups.id, { onDelete: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const salaryPaymentMethods = mysqlTable("salary_payment_methods", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  paymentMethod: varchar("payment_method", { length: 255 }).notNull(),
  isDefault: boolean("default").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** payroll_cycles — NO company_id (legacy). */
export const payrollCycles = mysqlTable("payroll_cycles", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  cycle: varchar("cycle", { length: 255 }),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeePayrollCycles = mysqlTable("employee_payroll_cycles", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  payrollCycleId: bigint("payroll_cycle_id", { mode: "number", unsigned: true }).references(
    () => payrollCycles.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeeMonthlySalaries = mysqlTable("employee_monthly_salaries", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  annualSalary: varchar("annual_salary", { length: 255 }),
  amount: varchar("amount", { length: 255 }).default("0").notNull(),
  basicSalary: varchar("basic_salary", { length: 255 }),
  basicValueType: mysqlEnum("basic_value_type", ["fixed", "ctc_percent"]),
  fixedAllowance: varchar("fixed_allowance", { length: 255 }).notNull(),
  type: mysqlEnum("type", ["initial", "increment", "decrement"]).default("initial").notNull(),
  date: date("date"),
  allowGeneratePayroll: mysqlEnum("allow_generate_payroll", ["yes", "no"]).default("yes").notNull(),
  effectiveAnnualSalary: varchar("effective_annual_salary", { length: 255 }),
  effectiveMonthlySalary: varchar("effective_monthly_salary", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** employee_variable_salaries — NO company_id (legacy). */
export const employeeVariableSalaries = mysqlTable("employee_variable_salaries", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  monthlySalaryId: bigint("monthly_salary_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => employeeMonthlySalaries.id, { onDelete: "cascade" }),
  variableComponentId: bigint("variable_component_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => salaryComponents.id, { onDelete: "cascade" }),
  variableValue: varchar("variable_value", { length: 255 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const salarySlips = mysqlTable("salary_slips", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  currencyId: int("currency_id", { unsigned: true }), // FK → currencies (deferred, 1.7)
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  salaryGroupId: bigint("salary_group_id", { mode: "number", unsigned: true }).references(
    () => salaryGroups.id,
    { onDelete: "cascade" },
  ),
  basicSalary: varchar("basic_salary", { length: 255 }).default("0").notNull(),
  netSalary: varchar("net_salary", { length: 255 }).default("0").notNull(),
  month: varchar("month", { length: 255 }).notNull(),
  year: varchar("year", { length: 255 }).notNull(),
  paidOn: date("paid_on"),
  status: mysqlEnum("status", ["generated", "review", "locked", "paid"])
    .default("generated")
    .notNull(),
  salaryJson: text("salary_json"),
  extraJson: text("extra_json"),
  expenseClaims: varchar("expense_claims", { length: 255 }).default("0").notNull(),
  payDays: int("pay_days").notNull(),
  salaryPaymentMethodId: bigint("salary_payment_method_id", {
    mode: "number",
    unsigned: true,
  }).references(() => salaryPaymentMethods.id, { onDelete: "set null" }),
  tds: double("tds", { precision: 16, scale: 2 }).notNull(),
  monthlySalary: double("monthly_salary", { precision: 16, scale: 2 }).notNull(),
  grossSalary: double("gross_salary", { precision: 16, scale: 2 }).notNull(),
  totalDeductions: double("total_deductions", { precision: 16, scale: 2 }).notNull(),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  salaryFrom: datetime("salary_from"),
  salaryTo: datetime("salary_to"),
  payrollCycleId: bigint("payroll_cycle_id", { mode: "number", unsigned: true }).references(
    () => payrollCycles.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  fixedAllowance: double("fixed_allowance").default(0).notNull(),
  expensesCreated: boolean("expenses_created").default(false).notNull(),
  expenseId: int("expense_id", { unsigned: true }), // FK → expenses (deferred, 1.7)
  additionalEarningJson: text("additional_earning_json"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const payrollSettings = mysqlTable("payroll_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  tdsSalary: varchar("tds_salary", { length: 255 }).notNull(),
  tdsStatus: boolean("tds_status").notNull(),
  financeMonth: varchar("finance_month", { length: 255 }).default("04").notNull(),
  extraFields: text("extra_fields"),
  semiMonthlyStart: int("semi_monthly_start").default(1),
  semiMonthlyEnd: int("semi_monthly_end").default(30),
  currencyId: int("currency_id", { unsigned: true }), // FK → currencies (deferred, 1.7)
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** payroll_global_settings — GLOBAL license/settings (no company_id). */
export const payrollGlobalSettings = mysqlTable("payroll_global_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  purchaseCode: varchar("purchase_code", { length: 255 }),
  licenseType: varchar("license_type", { length: 20 }),
  supportedUntil: timestamp("supported_until"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const companyPayrollEntities = mysqlTable(
  "company_payroll_entities",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }),
    name: varchar("name", { length: 255 }).notNull(),
    code: varchar("code", { length: 20 }),
    active: boolean("active").default(true).notNull(),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("company_payroll_entities_company_id_index").on(t.companyId)],
);

export const payCodes = mysqlTable("pay_codes", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }),
  code: varchar("code", { length: 255 }),
  time: double("time"),
  fixed: boolean("fixed").default(false).notNull(),
  fixedAmount: double("fixed_amount"),
  description: text("description"),
  regularFixedAmount: decimal("regular_fixed_amount", { precision: 8, scale: 2 })
    .default("0")
    .notNull(),
  regularTimeRate: decimal("regular_time_rate", { precision: 8, scale: 2 }).default("1.0").notNull(),
  weekendFixedAmount: decimal("weekend_fixed_amount", { precision: 8, scale: 2 })
    .default("0")
    .notNull(),
  weekendTimeRate: decimal("weekend_time_rate", { precision: 8, scale: 2 }).default("1.5").notNull(),
  holidayFixedAmount: decimal("holiday_fixed_amount", { precision: 8, scale: 2 })
    .default("0")
    .notNull(),
  holidayTimeRate: decimal("holiday_time_rate", { precision: 8, scale: 2 }).default("2.0").notNull(),
  dayOffFixedAmount: decimal("day_off_fixed_amount", { precision: 8, scale: 2 })
    .default("0")
    .notNull(),
  dayOffTimeRate: decimal("day_off_time_rate", { precision: 8, scale: 2 }).default("1.75").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const overtimePolicies = mysqlTable("overtime_policies", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  payCodeId: bigint("pay_code_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => payCodes.id, { onDelete: "cascade", onUpdate: "cascade" }),
  workingDays: boolean("working_days").default(false).notNull(),
  name: varchar("name", { length: 255 }),
  weekEnd: boolean("week_end").default(false).notNull(),
  holiday: boolean("holiday").default(false).notNull(),
  requestBeforeDays: int("request_before_days"),
  allowReportingManager: boolean("allow_reporting_manager").default(false).notNull(),
  allowRoles: text("allow_roles"),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const overtimePolicyEmployees = mysqlTable("overtime_policy_employees", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  overtimePolicyId: bigint("overtime_policy_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => overtimePolicies.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const overtimeRequests = mysqlTable("overtime_requests", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  overtimePolicyId: bigint("overtime_policy_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => overtimePolicies.id, { onDelete: "cascade", onUpdate: "cascade" }),
  date: date("date"),
  hours: double("hours").default(0).notNull(),
  minutes: double("minutes").default(0).notNull(),
  amount: double("amount").default(0).notNull(),
  overtimeReason: text("overtime_reason"),
  type: mysqlEnum("type", ["working", "holiday", "dayoff"]).default("working").notNull(),
  status: mysqlEnum("status", ["accept", "reject", "pending"]).default("pending").notNull(),
  saveType: mysqlEnum("save_type", ["draft", "save"]).default("draft").notNull(),
  startDate: date("start_date"),
  endDate: date("end_date"),
  actionBy: int("action_by", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  batchKey: varchar("batch_key", { length: 20 }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
